/**
 * @engine/input — Input Processor
 *
 * Processors modify control values before they reach actions.
 * Common uses: deadzone, normalization, inverting, scaling.
 */

import type { ProcessorDef, ProcessorType, ControlValue } from '../types';

/**
 * Interface for input processors
 */
export interface IInputProcessor {
  /** Processor type identifier */
  readonly type: ProcessorType;

  /**
   * Process a value
   */
  process(value: ControlValue): ControlValue;

  /**
   * Serialize to definition
   */
  toDefinition(): ProcessorDef;
}

/**
 * Abstract base class for processors
 */
export abstract class InputProcessor implements IInputProcessor {
  abstract readonly type: ProcessorType;
  protected params: Record<string, unknown>;

  constructor(params: Record<string, unknown> = {}) {
    this.params = params;
  }

  abstract process(value: ControlValue): ControlValue;

  toDefinition(): ProcessorDef {
    return {
      type: this.type,
      params: Object.keys(this.params).length > 0 ? { ...this.params } : undefined,
    };
  }

  /**
   * Helper: extract number from value
   */
  protected toNumber(value: ControlValue): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'boolean') return value ? 1 : 0;
    return 0;
  }

  /**
   * Helper: check if value is Vec2
   */
  protected isVec2(value: ControlValue): value is { x: number; y: number } {
    return typeof value === 'object' && 'x' in value && 'y' in value && !('z' in value);
  }

  /**
   * Helper: check if value is Vec3
   */
  protected isVec3(value: ControlValue): value is { x: number; y: number; z: number } {
    return typeof value === 'object' && 'x' in value && 'z' in value;
  }
}

/**
 * Processor registry
 */
const processorRegistry = new Map<ProcessorType, new (params?: Record<string, unknown>) => IInputProcessor>();

/**
 * Register a processor type
 */
export function registerProcessor(
  type: ProcessorType,
  ctor: new (params?: Record<string, unknown>) => IInputProcessor
): void {
  processorRegistry.set(type, ctor);
}

/**
 * Create a processor from definition
 */
export function createProcessor(def: ProcessorDef): IInputProcessor {
  const ctor = processorRegistry.get(def.type);
  if (!ctor) {
    throw new Error(`Unknown processor type: ${def.type}`);
  }
  return new ctor(def.params);
}
